import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BetCard, { Bet } from './BetCard';
import './LandingPage.css';
import { useActiveSubscription } from '../hooks/useActiveSubscription';
import { apiUrl } from '../config/api';
import { subscriptionPlans } from '../config/stripe';

const HERO_CARD_LIMIT = 2;
const BEST_VALUE_THRESHOLD = 0.01;

interface LandingOddsResponse {
  heroBets: Bet[];
}

const LandingPage: React.FC = () => {
  const { user, loading } = useAuth();
  const { checkingSub, hasActiveSub } = useActiveSubscription();
  const [heroBets, setHeroBets] = useState<Bet[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [errorHero, setErrorHero] = useState<string | null>(null);

  const loadLandingOdds = useCallback(async () => {
    const response = await fetch(apiUrl('/api/landing-odds'));
    if (!response.ok) {
      let message = 'Failed to load the public sample.';
      try {
        const payload = await response.json();
        if (payload && typeof payload.error === 'string') {
          message = payload.error;
        }
      } catch {
        // Use default message when response is not JSON
      }
      throw new Error(message);
    }

    const payload = (await response.json()) as LandingOddsResponse;
    return (payload.heroBets ?? []).slice(0, HERO_CARD_LIMIT);
  }, []);

  useEffect(() => {
    if (loading || checkingSub || (user && hasActiveSub)) return;
    let cancelled = false;

    const loadInitial = async () => {
      setLoadingInitial(true);
      setErrorHero(null);
      try {
        const nextHeroBets = await loadLandingOdds();
        if (!cancelled) {
          setHeroBets(nextHeroBets);
        }
      } catch (error) {
        if (cancelled) return;
        setHeroBets([]);
        setErrorHero(error instanceof Error ? error.message : 'Failed to load the public sample.');
      } finally {
        if (!cancelled) {
          setLoadingInitial(false);
        }
      }
    };

    loadInitial();
    return () => {
      cancelled = true;
    };
  }, [checkingSub, hasActiveSub, loadLandingOdds, loading, user]);

  const getHeroSpotLabel = (bet: Bet, index: number) => {
    if (index === 0 && heroBets.length === 1 && (bet.expectedValue ?? -Infinity) < BEST_VALUE_THRESHOLD) {
      return 'Worst value';
    }
    return index === 0 ? 'Best value' : 'Worst value';
  };

  // Avoid flicker while determining auth/subscription state
  if (loading || checkingSub) {
    return null;
  }

  // Subscribed, logged-in users should never see the landing page
  if (user && hasActiveSub) {
    return <Navigate to="/ev" replace />;
  }

  return (
    <div className="landing-page">
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content">
              <h1 className="hero-title"> Find value before you bet.</h1>
              <p className="hero-subtitle">
                Analyze live sportsbook lines, spot positive value opportunites, and avoid sucker bets.
              </p>
              <div className="hero-cta">
                {user ? (
                  <>
                    <Link to="/ev" className="btn btn-primary">
                      Go to EV board
                    </Link>
                    <Link to="/onboarding" className="btn btn-secondary">
                      Manage plan
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/onboarding" className="btn btn-primary plausible-event-name=landing-page-hero-click-sign-up">
                      Start free trial
                    </Link>
                    <Link to="/login" className="btn btn-secondary plausible-event-name=landing-page-hero-click-login">
                      Log in
                    </Link>
                  </>
                )}
              </div>
            </div>

            <div className="hero-board">
              <div className="hero-board__grid">
                {heroBets.slice(0, HERO_CARD_LIMIT).map((bet, index) => (
                  <div key={bet.id} className="hero-spot">
                    <p className="hero-spot-label">{getHeroSpotLabel(bet, index)}</p>
                    <BetCard bet={bet} analyticsVisibility="full" />
                  </div>
                ))}
                {loadingInitial && (
                  <>
                    <div className="card-skeleton hero-card-skeleton" />
                    <div className="card-skeleton hero-card-skeleton" />
                  </>
                )}
              </div>
              {errorHero && (
                <div className="no-results left">
                  <p>{errorHero}</p>
                </div>
              )}
              {!loadingInitial && !errorHero && heroBets.length === 0 && (
                <div className="no-results left">
                  <p>No qualifying public sample is available right now. The board updates as markets refresh.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="pricing-section">
        <div className="container">
          <h2 className="section-title">Premium EV tools without premium-tool pricing</h2>

          <div className="pricing-grid">
            {Object.entries(subscriptionPlans).map(([key, plan]) => {
              const monthlyEquivalent = plan.interval === 'year' ? plan.price / 12 : plan.price;
              const planTitle = key === 'monthly' ? 'Monthly access' : 'Yearly access';
              const planSupport =
                plan.interval === 'year'
                  ? `About $${monthlyEquivalent.toFixed(2)}/month billed yearly`
                  : 'Use the full EV board month to month';
              return (
                <article key={key} className={`pricing-card ${key === 'monthly' ? 'featured' : ''}`}>
                  <p className="pricing-card__eyebrow">{key === 'monthly' ? 'Most flexible' : 'Best value'}</p>
                  <h3 className="pricing-card__title">{planTitle}</h3>
                  <p className="pricing-card__price">
                    ${plan.price.toFixed(2)} <span>per {plan.interval}</span>
                  </p>
                  <p className="pricing-card__support">{planSupport}</p>
                  <ul className="pricing-card__list">
                    <li>Live full EV board access</li>
                    <li>Filter by sport, event, market, and sportsbook</li>
                    <li>Fair odds, win probability, and EV context</li>
                  </ul>
                  <Link to="/onboarding" className="btn btn-primary plausible-event-name=landing-page-pricing-click-sign-up">
                    Start free trial
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently asked questions</h2>
          <div className="faq-grid" data-nosnippet>
            <div className="faq-item">
              <h3>What does positive EV mean?</h3>
              <p>
                It means a sportsbook price looks better than its fair probability. Pikm surfaces those gaps so you can compare lines faster.
              </p>
            </div>
            <div className="faq-item">
              <h3>Is Pikm a pick service?</h3>
              <p>
                No. Pikm is a price-comparison tool, not a guarantee. Odds move, results vary, and disciplined bankroll limits still matter.
              </p>
            </div>
            <div className="faq-item">
              <h3>How is Pikm different?</h3>
              <p>
                Bigger EV tools often sell broad betting suites. Pikm focuses on a lean EV board, practical filters, and lower monthly pricing.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;